import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, AlertTriangle, FileText, Calendar, User, ChevronLeft, ChevronRight, ChevronUp, Download, ExternalLink, Shield, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import AppHeader from '@/components/AppHeader';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animated, filled-green checkmark (Cloudflare-style success tick)
function AnimatedCheckmark({ className = '' }) {
  return (
    <svg
      className={`animated-checkmark ${className}`}
      viewBox="0 0 80 80"
      xmlns="http://www.w3.org/2000/svg"
      data-testid="status-icon"
    >
      <circle className="animated-checkmark__circle" cx="40" cy="40" r="36" />
      <path className="animated-checkmark__check" d="M24 41l11 11 21-21" />
    </svg>
  );
}

export default function VerifyDocument() {
  const { verificationId } = useParams();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState('');
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfLoading, setPdfLoading] = useState(true);
  
  // Mobile bottom sheet state
  const [sheetPosition, setSheetPosition] = useState('half');
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const sheetRef = useRef(null);

  useEffect(() => {
    fetchDocument();
  }, [verificationId]);

  const fetchDocument = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/documents/${verificationId}`);
      setDocument(response.data);
      setPdfUrl(`${BACKEND_URL}${response.data.pdf_url}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Document not found');
    } finally {
      setLoading(false);
    }
  };

  // PDF load success handler
  const onDocumentLoadSuccess = useCallback(({ numPages: totalPages }) => {
    console.log('PDF loaded with', totalPages, 'pages');
    setNumPages(totalPages);
    setPageNumber(1);
    setPdfLoading(false);
  }, []);

  // Page navigation handlers
  const goToPrevPage = useCallback(() => {
    setPageNumber(prev => Math.max(1, prev - 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setPageNumber(prev => Math.min(numPages || 1, prev + 1));
  }, [numPages]);

  const getStatusConfig = (status) => {
    switch (status) {
      case 'Valid':
        return {
          icon: CheckCircle2,
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          badgeBg: 'bg-green-100',
          label: 'VERIFIED AUTHENTIC',
          description: 'This document has been verified as authentic'
        };
      case 'Revoked':
        return {
          icon: XCircle,
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          badgeBg: 'bg-red-100',
          label: 'DOCUMENT REVOKED',
          description: 'This document has been revoked and is no longer valid'
        };
      case 'Invalid':
        return {
          icon: AlertTriangle,
          color: 'text-amber-600',
          bgColor: 'bg-amber-50',
          borderColor: 'border-amber-200',
          badgeBg: 'bg-amber-100',
          label: 'INVALID DOCUMENT',
          description: 'This document could not be verified'
        };
      default:
        return {
          icon: FileText,
          color: 'text-slate-600',
          bgColor: 'bg-slate-50',
          borderColor: 'border-slate-200',
          badgeBg: 'bg-slate-100',
          label: 'UNKNOWN STATUS',
          description: 'Status could not be determined'
        };
    }
  };

  // Bottom sheet touch handlers
  const handleTouchStart = (e) => {
    setIsDragging(true);
    setStartY(e.touches[0].clientY);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const currentY = e.touches[0].clientY;
    const diff = startY - currentY;
    
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        if (sheetPosition === 'collapsed') setSheetPosition('half');
        else if (sheetPosition === 'half') setSheetPosition('expanded');
      } else {
        if (sheetPosition === 'expanded') setSheetPosition('half');
        else if (sheetPosition === 'half') setSheetPosition('collapsed');
      }
      setIsDragging(false);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const cycleSheetPosition = () => {
    if (sheetPosition === 'collapsed') setSheetPosition('half');
    else if (sheetPosition === 'half') setSheetPosition('expanded');
    else setSheetPosition('half');
  };

  const getSheetHeight = () => {
    switch (sheetPosition) {
      case 'collapsed': return 'h-24';
      case 'half': return 'h-[50vh]';
      case 'expanded': return 'h-[85vh]';
      default: return 'h-[50vh]';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Verifying document...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Verification Failed</h2>
          <p className="text-slate-600 mb-6">{error}</p>
          <p className="text-sm text-slate-500">The document could not be found in our system.</p>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(document.validity_status);
  const StatusIcon = statusConfig.icon;

  // Trust Summary Panel - inline component
  const renderTrustSummaryPanel = (isMobile = false) => (
    <div className={`space-y-4 ${isMobile ? '' : 'space-y-6'}`}>
      {/* Status Badge */}
      <div className={`${statusConfig.bgColor} ${statusConfig.borderColor} border-2 rounded-xl p-6 ${isMobile ? '' : 'p-8'} text-center`}>
        <div className={`w-16 h-16 ${isMobile ? 'w-14 h-14' : 'w-20 h-20'} ${statusConfig.badgeBg} rounded-full flex items-center justify-center mx-auto mb-3`}>
          {document.validity_status === 'Valid' ? (
            <AnimatedCheckmark className={isMobile ? 'w-9 h-9' : 'w-12 h-12'} />
          ) : (
            <StatusIcon className={`${isMobile ? 'w-7 h-7' : 'w-10 h-10'} ${statusConfig.color}`} data-testid="status-icon" />
          )}
        </div>
        <h2 className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold ${statusConfig.color} mb-1 tracking-wide`} data-testid="status-label">
          {statusConfig.label}
        </h2>
        <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-slate-600`}>{statusConfig.description}</p>
      </div>

      {/* Document Details */}
      <div className={`bg-white rounded-xl shadow-sm border border-slate-200 ${isMobile ? 'p-4' : 'p-6'}`}>
        <h3 className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold text-slate-900 mb-4 flex items-center gap-2`}>
          <Shield className="w-5 h-5 text-slate-400" />
          Document Details
        </h3>
        
        <div className="space-y-3">
          <div className="flex items-start gap-3 pb-3 border-b border-slate-100">
            <FileText className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-0.5">Document ID</p>
              <p className="font-mono text-sm font-medium text-slate-900 truncate" data-testid="document-id">
                {document.document_id}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 pb-3 border-b border-slate-100">
            <Calendar className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-0.5">Issue Date</p>
              <p className="text-sm font-medium text-slate-900" data-testid="issue-date">
                {new Date(document.issue_date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <User className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-0.5">Generated By</p>
              <p className="text-sm font-medium text-slate-900" data-testid="generated-by">
                {document.generated_by}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Verification ID */}
      <div className={`bg-slate-900 rounded-xl ${isMobile ? 'p-4' : 'p-5'} text-white`}>
        <p className="text-xs text-slate-400 uppercase tracking-widest mb-1.5">Verification ID</p>
        <p className="font-mono text-xs break-all leading-relaxed" data-testid="verification-id">{document.verification_id}</p>
      </div>

      {/* Action Buttons */}
      <div className={`flex gap-3 ${isMobile ? '' : 'flex-col'}`}>
        <Button
          onClick={() => window.open(pdfUrl, '_blank')}
          className={`${isMobile ? 'flex-1' : 'w-full'} h-11 bg-amber-700 hover:bg-amber-800 text-white font-medium`}
          data-testid="download-pdf-button"
        >
          <Download className="w-4 h-4 mr-2" />
          Download PDF
        </Button>
        <Button
          onClick={() => window.open(pdfUrl, '_blank')}
          variant="outline"
          className={`${isMobile ? 'flex-1' : 'w-full'} h-11`}
        >
          <ExternalLink className="w-4 h-4 mr-2" />
          Open in New Tab
        </Button>
      </div>
    </div>
  );

  // PDF Preview - rendered inline for proper state management
  const renderPdfPreview = (width = 500, height = 600) => (
    <div className="bg-white rounded-lg overflow-hidden shadow-lg border border-slate-200">
      {pdfUrl ? (
        <div className="flex flex-col">
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={(error) => console.error('PDF load error:', error)}
            loading={
              <div className="flex items-center justify-center" style={{ height }}>
                <div className="text-center">
                  <FileText className="w-10 h-10 text-slate-400 mx-auto mb-3 animate-pulse" />
                  <p className="text-slate-600 text-sm">Loading preview...</p>
                </div>
              </div>
            }
            error={
              <div className="flex flex-col items-center justify-center p-6 text-center" style={{ height }}>
                <FileText className="w-12 h-12 text-slate-400 mb-3" />
                <p className="text-slate-900 font-medium mb-1 text-sm">Preview unavailable</p>
                <p className="text-slate-600 text-xs mb-3">Use the download button to view</p>
              </div>
            }
          >
            <Page 
              key={`page-${pageNumber}`}
              pageNumber={pageNumber} 
              width={width}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Document>
          
          {/* Page Navigation - Always show if we have page count, disable buttons appropriately */}
          <div className="flex items-center justify-between p-3 bg-slate-50 border-t">
            <Button
              onClick={goToPrevPage}
              disabled={!numPages || pageNumber <= 1}
              variant="outline"
              size="sm"
              className="flex items-center gap-1"
              data-testid="prev-page-btn"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Prev</span>
            </Button>
            
            <div className="flex items-center gap-2">
              {pdfLoading ? (
                <span className="text-xs text-slate-500">Loading...</span>
              ) : numPages ? (
                <>
                  <span className="text-sm font-medium text-slate-900" data-testid="page-indicator">
                    {pageNumber}
                  </span>
                  <span className="text-xs text-slate-500">of</span>
                  <span className="text-sm font-medium text-slate-900">
                    {numPages}
                  </span>
                  {numPages === 1 && (
                    <span className="text-xs text-slate-400 ml-1">(single page)</span>
                  )}
                </>
              ) : (
                <span className="text-xs text-slate-500">No pages</span>
              )}
            </div>
            
            <Button
              onClick={goToNextPage}
              disabled={!numPages || pageNumber >= numPages}
              variant="outline"
              size="sm"
              className="flex items-center gap-1"
              data-testid="next-page-btn"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center" style={{ height }}>
          <div className="text-center">
            <FileText className="w-10 h-10 text-slate-400 mx-auto mb-3" />
            <p className="text-slate-600 text-sm">Loading...</p>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* DESKTOP LAYOUT (lg and above) */}
      <div className="hidden lg:block min-h-screen bg-slate-50">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <AppHeader title="Vistara Verify" subtitle="Official Document Verification" />
            <Link 
              to="/search" 
              className="p-2.5 hover:bg-slate-100 rounded-lg transition-colors"
              title="Search Documents"
              data-testid="header-search-btn"
            >
              <Search className="w-5 h-5 text-slate-600" />
            </Link>
          </div>
        </header>

        {/* Main Content - Two Column Layout */}
        <main className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-5 gap-8">
            {/* Left Column: Trust Summary Panel (2/5 width) */}
            <div className="col-span-2">
              <div className="sticky top-24">
                {renderTrustSummaryPanel(false)}
              </div>
            </div>

            {/* Right Column: Document Preview (3/5 width) */}
            <div className="col-span-3">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-slate-900">Document Preview</h3>
                <p className="text-sm text-slate-500">
                  {numPages ? `${numPages} page${numPages > 1 ? 's' : ''} • Currently viewing page ${pageNumber}` : 'Loading document...'}
                </p>
              </div>
              {renderPdfPreview(550, 700)}
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-white border-t border-slate-200 mt-8">
          <div className="max-w-7xl mx-auto px-6 py-6 text-center text-sm text-slate-500">
            <p>This is an official verification page by Formium Alliance</p>
            <p className="mt-1">© 2025 Formium Alliance. All rights reserved.</p>
          </div>
        </footer>
      </div>

      {/* MOBILE LAYOUT (below lg) - Bottom Sheet Design */}
      <div className="lg:hidden min-h-screen bg-slate-100 flex flex-col relative overflow-hidden">
        {/* Fixed Header */}
        <header className="bg-white border-b border-slate-200 px-4 py-3 flex-shrink-0 z-20 flex items-center justify-between">
          <AppHeader title="Vistara Verify" subtitle="Verification" />
          <Link 
            to="/search" 
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            title="Search Documents"
            data-testid="header-search-btn-mobile"
          >
            <Search className="w-5 h-5 text-slate-600" />
          </Link>
        </header>

        {/* Fixed PDF Preview Area */}
        <div className="flex-1 p-4 pb-28 overflow-hidden flex flex-col items-center justify-start">
          <div className="w-full max-w-sm">
            {renderPdfPreview(320, 400)}
          </div>
        </div>

        {/* Bottom Sheet */}
        <div 
          ref={sheetRef}
          className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl z-30 transition-all duration-300 ease-out ${getSheetHeight()}`}
          style={{ boxShadow: '0 -10px 40px rgba(0,0,0,0.15)' }}
        >
          {/* Drag Handle */}
          <div 
            className="flex flex-col items-center py-3 cursor-grab active:cursor-grabbing touch-none"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onClick={cycleSheetPosition}
          >
            <div className="w-12 h-1.5 bg-slate-300 rounded-full mb-1" />
            <div className="flex items-center gap-1 text-slate-400">
              <ChevronUp className={`w-4 h-4 transition-transform ${sheetPosition === 'expanded' ? 'rotate-180' : ''}`} />
              <span className="text-xs">
                {sheetPosition === 'collapsed' ? 'Swipe up for details' : 
                 sheetPosition === 'half' ? 'Swipe for more' : 'Swipe down to close'}
              </span>
            </div>
          </div>

          {/* Sheet Content */}
          <div className="px-4 pb-8 overflow-y-auto" style={{ height: 'calc(100% - 52px)' }}>
            {renderTrustSummaryPanel(true)}
          </div>
        </div>
      </div>
    </>
  );
}
